import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, map, startWith, take } from 'rxjs/operators';
import { OrdersService } from 'src/app/lmo/services/orders.service';
import { SmartDropdownComponent } from '~common/smart-dropdown/smart-dropdown.component';
import { CreateTruckRequest } from '~proto/truck/truck_api_pb';
import { Truck } from '~proto/truck/truck_pb';
import { fuse } from '~utilities/fuse';
import { trackById } from '~utilities/trackById';
import { TruckAndTrailerService } from '../../services/truck-and-trailer.service';
import {
  AssignedAssetActionsComponent,
  ConfirmEvent,
} from '../assigned-asset-actions/assigned-asset-actions.component';

const searchOptions: Fuse.FuseOptions<Truck.AsObject> = {
  distance: 100,
  keys: ['name'],
  location: 0,
  maxPatternLength: 16,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.1,
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ct-order-details-edit-truck-selector',
  styleUrls: ['./order-details-edit-truck-selector.component.scss'],
  templateUrl: './order-details-edit-truck-selector.component.html',
})
export class OrderDetailsEditTruckSelectorComponent implements OnInit {
  @Output() public selected = new EventEmitter<{ truck: Truck.AsObject }>();
  @Input() public truck: Truck.AsObject;
  @Input() public invalid = false;
  @ViewChild(SmartDropdownComponent, { static: true }) private smartDropdown: SmartDropdownComponent;
  public trucks$: Observable<Truck.AsObject[]>;
  public trackById = trackById;
  public textChanges$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private truckService: TruckAndTrailerService,
    private orderService: OrdersService,
    private matDialog: MatDialog,
  ) {}

  public ngOnInit() {
    this.trucks$ = combineLatest([
      this.truckService.trucks$.pipe(map(fuse<Truck.AsObject>(searchOptions))),
      this.textChanges$$.pipe(
        debounceTime(100),
        startWith(''),
      ),
      this.orderService.currentOrder$,
    ]).pipe(
      map(([fused, text]) => {
        if (!text || text === '') {
          return fused.data;
        }
        return fused.fuse.search(text);
      }),
    );
  }

  public addTruck() {
    const name = this.textChanges$$.value;
    if (!name || name === '') {
      return;
    }
    const request = new CreateTruckRequest();
    request.setName(name);
    this.truckService.addTruck$(request).subscribe((truck) => {
      this.select(truck);
    });
  }

  public async select(truck: Truck.AsObject) {
    if (truck.isAssigned) {
      this.orderService.currentOrder$
        .pipe(
          filter((order) => !!order),
          take(1),
        )
        .subscribe(async (currentOrder) => {
          if (currentOrder.truck.id !== truck.id) {
            // truck is in use, prompt reassign action
            const reassignDialog = this.matDialog.open(AssignedAssetActionsComponent);
            reassignDialog.componentInstance.title = `Truck ${truck.name} Is Already In Use`;

            const confirm: ConfirmEvent = await reassignDialog.afterClosed().toPromise();
            if (!confirm.reassign) {
              // close without selecting truck
              this.smartDropdown.close();
              return;
            }

            // unassign truck, return to form with truck selected
            this.truckService.unassignTruck$(truck.id, confirm.complete).subscribe(() => {
              this.selected.emit({ truck });
              this.smartDropdown.close();
              return;
            });
          } else {
            // ALready on this order
            this.selected.emit({ truck });
            this.smartDropdown.close();
          }
        });
    } else {
      // truck not in use, can select and close dropdown
      this.selected.emit({ truck });
      this.smartDropdown.close();
    }
  }

  public inputTextChange(event: string) {
    this.textChanges$$.next(event.trim());
  }
}
